import { BaseElement, defineElement } from "./lib-element-js.min.js";

/*
 * Bilder-Lazyload.
 *
 * 17.10.2026 – deutlich kleinere Rolle als vorher.
 * Die meisten Bilder tragen ihre Adressen jetzt direkt im srcset und werden vom
 * Browser selbst geladen und zurueckgehalten (loading="lazy"). Fuer die bleibt
 * hier nichts zu tun; das Element ist dann nur noch der Kasten, der das
 * Seitenverhaeltnis freihaelt.
 *
 * Zustaendig ist dieses Skript nur noch fuer Bilder mit data-srcset. Das sind
 * die zweiten Bilder der Produktkarten, die erst beim Darueberfahren sichtbar
 * werden. Traegt ein solches Bild data-hover-only und hat das Geraet keinen
 * Mauszeiger (Telefon, Tablet), wird es nie geladen.
 *
 * Die Groessenangabe kommt jetzt als data-sizes aus dem Theme. Vorher wurde sie
 * aus der Breite des 1x1-Platzhalters gemessen, was zu kleine Bilder ergab.
 */

const PRELOAD_MARGIN_PX = 1200;
const PRELOAD_MARGIN = PRELOAD_MARGIN_PX + "px";
const EAGER_VIEWPORT_FACTOR = 1.5;

const pending = new Set();
let watcherActive = false;
let scheduled = false;

function viewportHeight() {
  return window.innerHeight || document.documentElement.clientHeight || 800;
}

function viewportWidth() {
  return window.innerWidth || document.documentElement.clientWidth || 1200;
}

function canHover() {
  if (typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(hover: hover)").matches;
}

function runWatch() {
  scheduled = false;
  Array.from(pending).forEach(function (el) {
    if (el.isNearViewport()) el.setSrcSet();
  });
  if (pending.size === 0) stopWatcher();
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(runWatch);
}

function startWatcher() {
  if (watcherActive) return;
  watcherActive = true;
  window.addEventListener("scroll", schedule, { passive: true, capture: true });
  window.addEventListener("resize", schedule, { passive: true });
}

function stopWatcher() {
  if (!watcherActive) return;
  watcherActive = false;
  window.removeEventListener("scroll", schedule, { capture: true });
  window.removeEventListener("resize", schedule);
}

class ImgLazyLoad extends BaseElement {
  image = this.querySelector("img");

  // lifecycle hook
  connected() {
    if (!this.image) return;

    // Bild traegt sein srcset bereits selbst: der Browser erledigt den Rest.
    if (!this.image.getAttribute("data-srcset")) return;

    // Zweitbild der Produktkarte auf einem Geraet ohne Mauszeiger: nie laden.
    if (this.image.getAttribute("data-hover-only") && !canHover()) return;

    if (this.isNearInitialViewport()) {
      this.setSrcSet();
      return;
    }

    this.observe();
    pending.add(this);
    startWatcher();
  }

  // reactive attributes/properties
  properties() {
    return {
      loaded: false,
    };
  }

  isNearInitialViewport() {
    const rect = this.getBoundingClientRect();
    return rect.top < viewportHeight() * EAGER_VIEWPORT_FACTOR;
  }

  isNearViewport() {
    const rect = this.getBoundingClientRect();
    const vh = viewportHeight();
    const vw = viewportWidth();
    const m = PRELOAD_MARGIN_PX;
    const verticallyNear = rect.top < vh + m && rect.bottom > -m;
    const horizontallyNear = rect.left < vw + m && rect.right > -m;
    return verticallyNear && horizontallyNear;
  }

  observe() {
    if (typeof IntersectionObserver === "undefined") {
      this.setSrcSet();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setSrcSet();
          }
        });
      },
      {
        rootMargin: PRELOAD_MARGIN,
      }
    );
    this.observer.observe(this);
  }

  setSrcSet() {
    if (this.loaded || !this.image) return;

    const srcset = this.image.getAttribute("data-srcset");
    if (!srcset) return;

    // Groessenangabe aus dem Theme, nicht aus der Breite des Platzhalters.
    const sizes =
      this.image.getAttribute("data-sizes") ||
      (this.image.offsetWidth || this.offsetWidth || 533) + "px";

    this.image.setAttribute("sizes", sizes);
    this.image.setAttribute("decoding", "async");
    this.image.setAttribute("loading", "lazy");
    this.image.setAttribute("srcset", srcset);
    this.loaded = true;

    this.cleanup();
  }

  cleanup() {
    pending.delete(this);
    if (pending.size === 0) stopWatcher();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

defineElement("img-lazy-load", ImgLazyLoad);